'use client';

import { useState, useRef, useEffect } from 'react';
import { UserDetails } from '@/utils/helpers';
import { CommentWithUser } from './types';
import { createComment, editComment } from './api';
import { MentionAutocomplete } from './MentionAutocomplete';
import { MentionOverlay } from './MentionOverlay';
import { Button } from '../Button/Button';
import ProfileAvatar from '../ProfileUser/ProfileAvatar';
import { useRouter } from 'next/navigation';

interface CommentFormProps {
  toneId: number;
  userDetails: UserDetails | null;
  parentId: number | null;
  editingComment?: CommentWithUser;
  onSubmit?: (id: number) => void;
  onCancel?: () => void;
  placeholder?: string;
  initialContent?: string;
}

export function CommentForm({
  toneId,
  userDetails,
  parentId,
  editingComment,
  onSubmit,
  onCancel,
  placeholder = 'Share your thoughts...',
  initialContent = '',
}: CommentFormProps) {
  const [content, setContent] = useState(editingComment?.content || initialContent);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const router = useRouter();

  // Mention autocomplete state
  const [showMentions, setShowMentions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 });
  const [mentionStartIndex, setMentionStartIndex] = useState(-1);

  const isEditing = !!editingComment;
  const isReply = parentId !== null && parentId !== undefined && parentId > 0;

  const contentEmpty = !content || content.trim() === '';
  const contentSameAsInitial = content === initialContent || content === editingComment?.content;

  // Focus textarea and position cursor after initial content when component mounts
  useEffect(() => {
    if (textareaRef.current) {
      // Always focus the textarea for reply forms or when editing
      if (isReply || isEditing) {
        textareaRef.current.focus();

        // If there's content, position cursor at the end
        const contentToCheck = isEditing ? content : initialContent;
        if (contentToCheck) {
          const length = contentToCheck.length;
          textareaRef.current.setSelectionRange(length, length);
        }

        // Trigger auto-resize
        textareaRef.current.style.height = 'auto';
        textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialContent, isReply, isEditing]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Redirect to login if not authenticated
    if (!userDetails) {
      router.push('/signin/email');
      return;
    }

    if (!content.trim() || isSubmitting) return;

    setIsSubmitting(true);
    let id: number | null = null;
    try {
      if (isEditing) {
        await editComment({
          commentId: editingComment!.id!,
          content: content.trim(),
        });
        id = editingComment!.id!;
      } else {
        const newComment = await createComment({
          toneId,
          parentId,
          content: content.trim(),
        });
        id = newComment.id!;
      }

      setContent('');

      // Reset textarea height after clearing content (defer to next tick)
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.style.height = 'auto';
          textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
        }
      }, 1);

      onSubmit?.(id);
    } catch (error) {
      console.error('Error submitting comment:', error);
      // TODO: Show error toast to user
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setContent(editingComment?.content || initialContent);
    onCancel?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSubmit(e as any);
    }
  };

  // Auto-resize textarea and handle mention detection
  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newContent = e.target.value;
    setContent(newContent);

    // Auto-resize
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }

    // Mention detection
    const cursorPosition = e.target.selectionStart;
    const textUpToCursor = newContent.slice(0, cursorPosition);
    const mentionMatch = textUpToCursor.match(/@(\w*)$/);

    if (mentionMatch) {
      const query = mentionMatch[1];
      const mentionStart = cursorPosition - mentionMatch[0].length;

      setMentionQuery(query);
      setMentionStartIndex(mentionStart);
      setShowMentions(true);

      // Calculate position for dropdown
      if (textareaRef.current) {
        const textarea = textareaRef.current;
        const rect = textarea.getBoundingClientRect();

        setMentionPosition({
          top: rect.height + 8,
          left: 0,
        });
      }
    } else {
      setShowMentions(false);
      setMentionQuery('');
      setMentionStartIndex(-1);
    }
  };

  // Handle mention selection
  const handleMentionSelect = (username: string) => {
    if (mentionStartIndex >= 0) {
      const beforeMention = content.slice(0, mentionStartIndex);
      const afterMention = content.slice(mentionStartIndex + mentionQuery.length + 1); // +1 for the @
      const newContent = beforeMention + `@${username} ` + afterMention;

      setContent(newContent);
      setShowMentions(false);
      setMentionQuery('');
      setMentionStartIndex(-1);

      // Focus back to textarea
      if (textareaRef.current) {
        textareaRef.current.focus();
        const newCursorPosition = beforeMention.length + username.length + 2; // +2 for @ and space
        setTimeout(() => {
          textareaRef.current?.setSelectionRange(newCursorPosition, newCursorPosition);
        }, 0);
      }
    }
  };

  const handleMentionClose = () => {
    setShowMentions(false);
    setMentionQuery('');
    setMentionStartIndex(-1);
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3 relative">
      <div className="flex gap-3">
        {!isEditing && (
          <ProfileAvatar
            username={userDetails?.username || ''}
            avatarUrl={userDetails?.avatar_url || ''}
            ownProfile={false}
            size={32}
          />
        )}

        <div className="flex flex-col gap-1 flex-1 relative">
          <div className="relative">
            {/* Placeholder text */}
            {!content && (
              <div
                className="absolute inset-0 px-3 py-2 text-zinc-400 pointer-events-none"
                style={{
                  lineHeight: '1.5',
                  fontSize: 'inherit',
                  fontFamily: 'inherit',
                }}
              >
                {placeholder}
              </div>
            )}

            <MentionOverlay content={content} className="rounded-lg" />
            <textarea
              ref={textareaRef}
              value={content}
              onChange={handleInput}
              onKeyDown={handleKeyDown}
              placeholder=""
              className="w-full border border-zinc-700 rounded-lg px-3 py-2 resize-none focus:outline-none relative z-1"
              rows={isReply ? 2 : 3}
              disabled={isSubmitting}
              name={parentId ? `comment-reply-to-${parentId}` : 'comment'}
              id={parentId ? `comment-reply-to-${parentId}` : 'comment'}
              style={{
                background: 'transparent',
                color: 'transparent',
                caretColor: 'white', // white for cursor visibility
              }}
            />
            {/* Background for textarea */}
            {/* <div className="absolute inset-0 bg-zinc-900 border border-zinc-700 rounded-lg -z-10" /> */}
          </div>

          {/* Mention Autocomplete */}
          <MentionAutocomplete
            isOpen={showMentions}
            query={mentionQuery}
            onSelect={handleMentionSelect}
            onClose={handleMentionClose}
            position={mentionPosition}
          />

          <div className="flex-col md:flex-row flex items-start justify-between gap-2">
            {!isReply ? (
              <span className="text-zinc-500 text-xs">
                @username to mention • Ctrl+Enter to submit
              </span>
            ) : (
              <span></span>
            )}

            <div className="flex gap-2 self-end">
              {(isEditing || isReply) && onCancel && (
                <Button
                  type="button"
                  onClick={handleCancel}
                  variant="slim"
                  disabled={isSubmitting}
                  isSecondary={true}
                  style={{
                    padding: '.75rem 1.25rem',
                  }}
                >
                  <span className="block">Cancel</span>
                </Button>
              )}

              <Button
                type="submit"
                variant="slim"
                isPrimary
                disabled={contentEmpty || contentSameAsInitial || isSubmitting}
                loading={isSubmitting}
                style={{
                  padding: '.75rem 1.25rem',
                }}
                tracking={{
                  event: 'comment.submitted',
                  properties: {
                    is_editing: isEditing,
                    is_reply: isReply,
                  },
                }}
              >
                <span className="block">
                  {isEditing ? 'Update' : isReply ? 'Reply' : 'Comment'}
                </span>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
}
